import { create } from 'zustand';
import type {
  TimelineProject, TimelineScene, TimelineShot, TimelineTransition, TransitionType,
  VideoShot, ScreenplayData, ShotCoverageLine,
} from '@/types/timeline.ts';
import type { UndoOptions } from './undoStore.ts';
import { useUndoStore } from './undoStore.ts';
import { useAiStore } from './aiStore.ts';
import { usePlaybackStore } from './playbackStore.ts';
import { isReadOnly } from './guards.ts';
import { useAudioStore } from './audioStore.ts';
import { capabilities } from '@/capabilities.ts';
import { getCachedAssetBytes } from '@/previs/assetStorage.ts';
import { encodeAudioBufferToWav, generatePeaks } from '@/audio/audioProcessor.ts';
import { stashBlob } from '@/previs/audioBlobStore.ts';
import { LANE_COLORS } from '@/audio/audioConstants.ts';
import { computeSceneNumbers, computeShotNumbers } from '@/utils/numberingFormatter.ts';
import {
  canonicalizeLocationName, normalizeLocationKey, nextDuplicateLocationName, renameLocationInSceneText,
} from '@/utils/locationName.ts';
import { canonicalizeCharacterName, normalizeCharacterKey, nextDuplicateCharacterName } from '@/utils/characterName.ts';
import { stashPendingTileUpdate } from './pendingTileUpdates.ts';
import { mergeTileVideo } from './tileVideoMerge.ts';
import { flushSceneReadyBuffer, setSceneReadyApplier } from './sceneReadyBuffer.ts';
import { recordStoreSet } from '@/perf/loadPerf.ts';

/** One shot's tile fields as Unity pushes them in a sceneReady payload. */
export interface SceneReadyShotUpdate {
  id: number;
  thumbnailDataUrl?: string;
  videoObjectUrl?: string;
  thumbHash?: string;
  videoHash?: string;
  /** 20261002 - true = this animatic shot has no playable video right now: clear the carried URL
   *  (a URL in the same push wins). See store/tileVideoMerge.ts. */
  videoCleared?: boolean;
}

/** Field-by-field `fresh ?? prev` merge of two pushes for the same shot (mirrors
 *  stashPendingTileUpdate) — a thumbnail-only push must not erase a video URL an earlier one brought.
 *  The video fields follow tileVideoMerge's rules (a later `videoCleared` takes an earlier URL away). */
function mergeTileUpdates(prev: SceneReadyShotUpdate, fresh: SceneReadyShotUpdate): SceneReadyShotUpdate {
  return {
    id: fresh.id,
    thumbnailDataUrl: fresh.thumbnailDataUrl ?? prev.thumbnailDataUrl,
    thumbHash: fresh.thumbHash ?? prev.thumbHash,
    ...mergeTileVideo(prev, fresh),
  };
}

/**
 * Apply a tile push to a shot, returning the SAME shot object when the merge changes nothing.
 * Identity is the skip signal markScenesReady reads: on a live-board refresh the sqliteBridge
 * carry-over has already put these exact file:// URLs on the rows (`?v=<mtime>` is stable), so
 * nearly every message in Unity's open-handshake burst lands here as a no-op and must not churn
 * the project reference.
 *
 * The comparison is of the MERGED value against the current one, which is what makes it safe to
 * include thumbHash/videoHash: the merge is monotone (a push that omits a field leaves the
 * current value in place), so an absent hash can never read as a change the way a raw
 * `fresh.thumbHash !== shot.thumbHash` would. Nothing sends hashes on this path today, but when
 * the hash rollout lands (see features/assets.ts) a hash-only push has to still apply.
 */
/**
 * Shots whose animatic was just reverted web-side (actions.revertAnimaticForEnhance) and whose
 * Unity-side revert has not yet been confirmed by a repush (20260903, T3 reviewer). Unity drops the
 * shot from animaticVideoFilePaths only when it PROCESSES revertAnimaticToSingleShot, a frame or
 * more after the web cleared videoObjectUrl — and any sceneReady for that scene in between (the
 * board-fill drain pushes one per scene continuously) still carries the animatic mp4. Left alone,
 * applyTileUpdate would graft that URL back onto a shot whose isAnimatic is already gone and whose
 * videoShots row is already dropped: a sticky half-state the refresh merge then carries forward
 * (fkUnchanged keeps the in-memory videoObjectUrl). While a shot is in this set the applier ignores
 * an incoming video URL/hash for it. Cleared by loadProject — the revert's own repush lands there,
 * after Unity has forgotten the mp4, so every later push is honest again.
 */
const animaticRevertPendingIds = new Set<number>();

export function markAnimaticRevertPending(shotId: number): void {
  animaticRevertPendingIds.add(shotId);
}

export function clearAnimaticRevertGuard(): void {
  animaticRevertPendingIds.clear();
}

/**
 * 20261002 - `videoCleared` (no URL beside it) clears the shot's videoObjectUrl and videoHash: Unity
 * deleted the animatic's stale MP4 after a project-wide look change, and the tile, the Viewer and the
 * Timeline all read the video straight off these two fields, so clearing them here is the whole of the
 * web-side clear. A later push with the re-rendered file's URL sets it again. See tileVideoMerge.ts.
 */
function applyTileUpdate(shot: TimelineShot, fresh: SceneReadyShotUpdate): TimelineShot {
  const thumbnailDataUrl = fresh.thumbnailDataUrl ?? shot.thumbnailDataUrl;
  const revertPending = animaticRevertPendingIds.has(shot.id);
  const video = revertPending
    ? { videoObjectUrl: shot.videoObjectUrl, videoHash: shot.videoHash }
    : mergeTileVideo({ videoObjectUrl: shot.videoObjectUrl, videoHash: shot.videoHash }, fresh);
  const { videoObjectUrl, videoHash } = video;
  const thumbHash = fresh.thumbHash ?? shot.thumbHash;
  if (thumbnailDataUrl === shot.thumbnailDataUrl && videoObjectUrl === shot.videoObjectUrl &&
      thumbHash === shot.thumbHash && videoHash === shot.videoHash) return shot;
  return { ...shot, thumbnailDataUrl, videoObjectUrl, thumbHash, videoHash };
}

/**
 * Recompute display scene numbers for an already-ordered scene list, RESPECTING locked state and manual
 * overrides (mirrors applyComputedNumbering / Unity's SceneAndShotNumberingManager):
 *  - Locked: numbers are frozen and travel with their scenes — do NOT resequence.
 *  - Unlocked: recompute from each scene's raw override via computeSceneNumbers, so a custom-numbered
 *    scene keeps its number and only auto-numbered scenes resequence around it.
 *
 * Shared by reorderScene and mergeSceneScreenplay — both change which scene sits at which index, and
 * both need the Board tab to read correctly in the frame before Unity's repush lands.
 */
function renumberScenesInOrder(
  scenes: TimelineScene[],
  documentSettings: TimelineProject['documentSettings'],
): TimelineScene[] {
  const startingNumber = documentSettings?.startingSceneNumber ?? 1;
  const locked = documentSettings?.isNumberingLocked ?? false;
  const sceneNumbers = locked
    ? scenes.map(s => s.number)
    : computeSceneNumbers(
        scenes.map(s => ({ number: s.numberOverride ?? '' })),
        startingNumber,
      );
  return scenes.map((s, i) => ({ ...s, number: sceneNumbers[i] }));
}

/**
 * Twin of renumberScenesInOrder for the shots inside one scene: recompute display letters for an
 * already-reordered shot list, respecting locked state and manual overrides.
 *  - Locked: Unity has written the computed letters into shots.number and they travel with the shot.
 *  - Unlocked: recompute from each shot's raw override via computeShotNumbers, so a custom-numbered
 *    shot keeps its number and only auto-lettered shots resequence around it.
 *
 * Used by reorderShotInScene. Without it a drag repaints the board with the old letters against the
 * new order ("B A C"), because Unity deliberately does not repush after a successful reorder.
 */
function renumberShotsInOrder(
  shots: TimelineShot[],
  documentSettings: TimelineProject['documentSettings'],
): TimelineShot[] {
  if (!documentSettings) return shots;
  if (documentSettings.isNumberingLocked) return shots;

  const shotNumbers = computeShotNumbers(
    shots.map(s => ({ number: s.numberOverride ?? '' })),
    documentSettings.useFullAlphabet,
    documentSettings.useDigitsForShots,
  );
  return shots.map((s, i) => ({ ...s, number: shotNumbers[i] }));
}

/**
 * Extract audio from a video MP4 and add it as an SFX lane clip.
 * Silently skips if the video has no audio track or decoding fails.
 */
async function extractAndStoreVideoAudio(videoObjectUrl: string, shotId: number, allocId: () => number): Promise<void> {
  try {
    const videoBytes = getCachedAssetBytes(videoObjectUrl);
    if (!videoBytes) return;

    const audioCtx = new AudioContext();
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await audioCtx.decodeAudioData(videoBytes.buffer.slice(0) as ArrayBuffer);
    } finally {
      audioCtx.close();
    }

    if (audioBuffer.duration < 0.1) return;

    const wavBytes = encodeAudioBufferToWav(audioBuffer);
    const peaks = generatePeaks(audioBuffer);

    const clipId = allocId();
    const clip = {
      id: clipId,
      label: 'Video Audio',
      shotId,
      shotOffset: 0,
      duration: audioBuffer.duration,
      volume: 1.0,
      bufferOffset: 0,
      audioBuffer,
      mimeType: 'audio/wav',
      lane: 2,
      isMuted: false,
      fadeInTime: 0,
      fadeOutTime: 0,
      color: LANE_COLORS[2],
      peaks,
    };

    stashBlob(clipId, wavBytes);
    useAudioStore.getState().addAudioClip(clip);
  } catch {
    // No audio track or decode failure — silently skip
  }
}

// ── Re-exported types (preserves import path for consumers) ─────────────
export type { TransitionAtTime, ContinuousInfo, ContinuousChainPair, ContinuousChain } from './transitionHelpers.ts';

import {
  type TransitionAtTime, type ContinuousInfo, type ContinuousChain, type ContinuousChainPair,
  TRANSITION_DURATIONS,
  getTransition as getTransitionPure,
  getTransitionAtTime as getTransitionAtTimePure,
  getContinuousChain as getContinuousChainPure,
  getActiveContinuousPair as getActiveContinuousPairPure,
  getContinuousInfo as getContinuousInfoPure,
} from './transitionHelpers.ts';

import {
  allShots as allShotsPure,
  getShotAtTime as getShotAtTimePure,
  getShotStartTime as getShotStartTimePure,
  getSceneForShot as getSceneForShotPure,
  getActiveScenes as getActiveScenesPure,
  getViewOffset as getViewOffsetPure,
  getViewDuration as getViewDurationPure,
  getScriptProgress as getScriptProgressPure,
  getAssetsForScene as getAssetsForScenePure,
} from './projectDerived.ts';

// ── Store ────────────────────────────────────────────────────────────────

// Board-open thumbnail fill (Unity boardFillState snapshots — see
// docs/superpowers/specs/2026-07-01-board-open-thumbnail-fill-progressive-design.md).
// Totals count only shots/scenes that were pending (missing disk thumbnail) when the
// Unity pass scanned; snapshots are absolute and replace this state wholesale.
export interface BoardFillState {
  pendingSceneIds: number[];
  scenesTotal: number;
  scenesDone: number;
  shotsTotal: number;
  shotsDone: number;
  // additive (cs:5308 contract): posters = phase 1 (one render per scene), full = phase 2; missing = full (pre-phase Unity)
  // 20260909 - `cast` is the pre-phase hold: Unity has asked the web to run its cast pipeline and is
  // holding the poster pass until it answers, so no render is in flight and the bar has nothing to count.
  // 20261002 - `animatics` is the post-thumbnail phase that re-renders the animatic videos a project-wide
  // look change (aspect / sensor / Look / tone mapping) made stale. It counts its own work in the two
  // fields below; shotsTotal/shotsDone are left exactly as the thumbnail phases ended them, so
  // fillPresentation's modal-vs-pill answer cannot flip mid-fill. An older Unity never sends any of this.
  phase?: 'posters' | 'full' | 'cast' | 'animatics';
  animaticsTotal?: number;
  animaticsDone?: number;
  complete: boolean;
}

/** Extras for `attachVideo` — everything here is optional and omitting it is the shipped behaviour. */
export interface AttachVideoOptions {
  /** Poster frame (base64 JPEG, no data: prefix) already extracted by the caller. */
  posterBase64?: string | null;
}

interface ProjectState {
  project: TimelineProject | null;
  // Monotonic count of COMMITTED project loads — every completed `loadProject` (actions.ts) bumps it
  // and NOTHING else may, so a caller can tell "the slim-DB merge landed" from "the project object was
  // replaced", which tile pushes (markScenesReady), ready-marks and every other in-place writer here
  // do constantly. Sole writer: loadProject.
  projectLoadSeq: number;
  selectedShotId: number | null;
  userInitiatedShotSelection: boolean;
  selectedAudioClipId: number | null;
  activeSceneId: number | null;
  videoShots: VideoShot[];
  readySceneIds: Set<number>;
  allScenesReady: boolean;
  // The load burst since the last project push has been flushed: Unity's allScenesReady landed.
  // OnProjectLoadedCoroutine sends it unconditionally, as the LAST push of every load, and AFTER
  // the boardFillState snapshot when there is one — so once this is true the fill's presentation
  // for the current load is final. Cleared by loadProject (every push, refresh included) and by
  // markLoadBurstPending (an in-project import's reimportComplete, whose repush is still to come).
  // NOT the same thing as allScenesReady: computeInitialSceneState sets that true synchronously on
  // every non-progressive load — i.e. every load the app actually does — so it cannot tell
  // "loaded" from "burst flushed". One consumer: GuideRail holds a guide's FIRST reveal on it
  // (owner batch 7, 20260903), so a card never paints one frame before a modal-sized fill snapshot.
  loadBurstSettled: boolean;
  boardFill: BoardFillState | null;
  // User took BoardFillModal's stall escape ("Continue in background") for the fill currently in
  // flight. Purely presentational — Unity keeps rendering either way; see fillPresentation().
  boardFillModalDismissed: boolean;
  readOnly: boolean;
  sessionId: string | null;
  sessionRevision: number;
  reviewSessionId: string | null;
  isCreator: boolean;
  originalBytes: Uint8Array | null;
  fileName: string;
  isSaving: boolean;
  saveError: string | null;
  customScriptTexts: Record<number, string>;
  editedScreenplaySceneIds: Set<number>;
  hasTeamAccess: boolean;
  teamAccessUrl: string | null;
  teamSessionId: string | null;
  teamRevision: number;
  teamSharePassword: string | null;
  _nextId: number;

  // ID allocator
  _allocId: (count?: number) => number;

  // Selection
  selectShot: (id: number | null, userInitiated?: boolean) => void;
  selectAudioClip: (id: number | null) => void;

  // Shot editing
  updateShotDuration: (id: number, newDuration: number, opts?: UndoOptions) => void;
  reorderShotInScene: (sceneId: number, shotId: number, newIndex: number) => void;
  /** 20260923 - board select-mode drag: move several shots of ONE scene as one contiguous block. */
  reorderShotsInScene: (sceneId: number, shotIds: number[], insertIndex: number) => void;
  appendShotNotes: (shotId: number, text: string) => void;

  // Transition actions
  getTransition: (fromShotId: number, toShotId: number) => TimelineTransition | null;
  getTransitionAtTime: (t: number) => TransitionAtTime | null;
  getContinuousInfo: (shotId: number) => ContinuousInfo | null;
  getContinuousChain: (shotId: number) => ContinuousChain | null;
  getActiveContinuousPair: (shotId: number) => ContinuousChainPair | null;
  setTransition: (fromShotId: number, toShotId: number, type: TransitionType) => void;

  // Video actions
  /** `opts.posterBase64` pre-empts the poster extraction — see the implementation's 20260909 note. */
  attachVideo: (shotId: number, objectUrl: string, mimeType: string, historyId?: string, opts?: AttachVideoOptions) => void;
  removeVideo: (shotId: number) => void;
  attachTransitionVideo: (fromShotId: number, toShotId: number, objectUrl: string, mimeType: string, historyId?: string) => void;
  removeTransitionVideo: (fromShotId: number, toShotId: number) => void;

  // Save actions
  setOriginalFile: (bytes: Uint8Array, name: string) => void;

  // Review mode
  setReadOnly: (readOnly: boolean) => void;
  setSessionId: (id: string | null) => void;
  setSessionRevision: (rev: number) => void;
  setReviewSessionId: (id: string | null) => void;
  setIsCreator: (isCreator: boolean) => void;

  // Progressive loading
  markSceneReady: (sceneId: number, shots: SceneReadyShotUpdate[]) => void;
  markScenesReady: (entries: Array<{ sceneId: number; shots: SceneReadyShotUpdate[] }>) => void;
  markAllScenesReady: () => void;
  /** A Unity push is coming that this store has not seen yet (reimportComplete precedes its repush). */
  markLoadBurstPending: () => void;
  markShotThumbnail: (shotId: number, url: string, hashes?: { thumbHash?: string; videoHash?: string }) => void;
  setBoardFillState: (state: BoardFillState) => void;
  dismissBoardFillModal: () => void;

  // Asset-freshness reconcile sweep (Task 14, dormant until Unity ships assetManifest)
  dropShots: (ids: number[]) => void;

  // Scene selector
  setActiveScene: (id: number | null) => void;
  getActiveScenes: () => TimelineScene[];
  getViewOffset: () => number;
  getViewDuration: () => number;

  // Derived helpers
  allShots: () => TimelineShot[];
  getShotAtTime: (t: number) => TimelineShot | null;
  getShotStartTime: (id: number) => number;
  getSceneForShot: (shotId: number) => { color: string; name: string; locationType: string; timeOfDay: string } | null;
  getScriptProgress: () => { scene: TimelineScene | null; progress: number };
  getAssetsForScene: (sceneId: number) => { videos: number; images: number; totalCredits: number };
  setCustomScriptText: (sceneId: number, text: string) => void;
  saveCustomScriptToScene: (sceneId: number) => void;
  markScreenplayEdited: (sceneId: number) => void;
  setTeamAccessState: (has: boolean, url: string | null, sessionId?: string | null, revision?: number, password?: string | null) => void;

  // Coverage (script lining)
  assignElementsToShot: (sceneId: number, elementIds: Array<{ type: 'dialogue' | 'action'; id: number }>, shotId: number) => void;
  unassignElementsFromShot: (sceneId: number, elementIds: Array<{ type: 'dialogue' | 'action'; id: number }>, shotId: number) => void;
  updateShotCoverageProps: (shotId: number, updates: Partial<{ coverageColor: string; shotFraming: string; shotSize: string; shotMovement: string }>) => void;
  setShotCoverageLines: (sceneId: number, shotId: number, lines: ShotCoverageLine[], coverageColor: string) => void;
  deleteScreenplayElements: (sceneId: number, elements: Array<{ type: 'dialogue' | 'action' | 'transition'; id: number }>) => void;
  updateScreenplayElementText: (sceneId: number, elementType: 'dialogue' | 'action' | 'transition', elementId: number, text: string) => void;
  updateDialogueCharacterName: (sceneId: number, dialogueId: number, characterName: string) => void;
  updateLocationDescription: (locationId: number, text: string) => void;
  updateSceneLocationNotes: (sceneId: number, text: string) => void;
  /** 20260923 - the per-scene pencil: rewrite ONE scene's own location text; its link is untouched. */
  setSceneLocationText: (sceneId: number, text: string) => void;
  /** 20260923 - the per-scene trash: unlink ONE scene from its location (FK 0); its text is kept. */
  unlinkSceneLocation: (sceneId: number) => void;
  addScreenplayLocation: (name: string) => void;
  renameScreenplayLocation: (locationId: number, name: string) => void;
  duplicateScreenplayLocation: (locationId: number) => void;
  deleteScreenplayLocation: (locationId: number) => void;
  mergeScreenplayLocation: (sourceLocationId: number, targetLocationId: number) => void;

  renameScreenplayCharacter: (characterId: number, name: string) => void;
  duplicateScreenplayCharacter: (characterId: number) => void;

  // Scene/character restructuring (View Changes mode)
  reorderScene: (sceneId: number, newIndex: number) => void;
  mergeSceneScreenplay: (sourceSceneId: number, targetSceneId: number) => void;
  mergeCharacterDialogue: (sourceCharName: string, targetCharName: string) => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  project: null,
  projectLoadSeq: 0,
  selectedShotId: null,
  userInitiatedShotSelection: false,
  selectedAudioClipId: null,
  activeSceneId: null,
  videoShots: [],
  readySceneIds: new Set<number>(),
  allScenesReady: false,
  loadBurstSettled: false,
  boardFill: null,
  boardFillModalDismissed: false,
  readOnly: false,
  sessionId: null,
  sessionRevision: 0,
  reviewSessionId: null,
  isCreator: false,
  originalBytes: null,
  fileName: '',
  isSaving: false,
  saveError: null,
  customScriptTexts: {},
  editedScreenplaySceneIds: new Set<number>(),
  hasTeamAccess: false,
  teamAccessUrl: null,
  teamSessionId: null,
  teamRevision: 0,
  teamSharePassword: null,
  _nextId: 1,

  _allocId: (count = 1) => {
    const id = get()._nextId;
    set({ _nextId: id + count });
    return id;
  },

  selectShot: (id, userInitiated = false) => set({ selectedShotId: id, userInitiatedShotSelection: userInitiated, ...(id !== null ? { selectedAudioClipId: null } : {}) }),

  selectAudioClip: (id) => set({ selectedAudioClipId: id, ...(id !== null ? { selectedShotId: null } : {}) }),

  updateShotDuration: (id, newDuration, opts) => {
    if (isReadOnly()) return;

    if (opts?.pushUndo !== false) useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const MIN_DURATION = 0.2;  // ~0.167s rounds to 0.2 at 0.1s precision (≥5 frames at 30fps)
    const MAX_DURATION = 60;
    const clamped = Math.min(MAX_DURATION, Math.max(MIN_DURATION, Math.round(newDuration * 10) / 10));

    const updatedScenes = project.scenes.map(scene => ({
      ...scene,
      shots: scene.shots.map(shot =>
        shot.id === id ? { ...shot, duration: clamped } : shot
      ),
    }));

    const totalDuration = updatedScenes.reduce(
      (sum, scene) => scene.isOmitted ? sum : sum + scene.shots.reduce((s, shot) => s + shot.duration, 0),
      0
    );

    set({ project: { ...project, scenes: updatedScenes, totalDuration } });

    // Animatic shots skip normal bridge sync — the regeneration round-trip handles them
    const shot = project.scenes.flatMap(s => s.shots).find(s => s.id === id);
    if (capabilities.bridge && !(shot?.isAnimatic)) {
      import('@/bridge/shotSyncBridge.ts').then(m => m.notifyDurationChange(id, clamped));
    }
  },

  reorderShotInScene: (sceneId, shotId, newIndex) => {
    if (isReadOnly()) return;

    if (capabilities.bridge) {
      // Undo is display-only (it re-sets web stores, it never re-sends a bridge verb) and in embedded
      // mode this mutation is committed natively and irreversibly. Every snapshot on the stack — not
      // just the one this action would push — now predates a change undo cannot revert, so drop the
      // history instead of offering a lie. Reorder deliberately never repushes on success
      // (TimelineWebViewPanel.HandleReorderShots), only on Unity's rejection, so this is the only clear
      // the success path ever gets. Mirrors reorderScene's policy (cs:5467).
      useUndoStore.getState().clearStacks();
    } else {
      // Standalone Timeline (ShotLane): no bridge, so the mutation is web-owned and genuinely
      // reversible. Undo stays honest here — do NOT collapse these two branches.
      useUndoStore.getState().pushSnapshot();
    }

    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId) return scene;

      const shots = [...scene.shots];
      const oldIndex = shots.findIndex(s => s.id === shotId);
      if (oldIndex < 0 || oldIndex === newIndex) return scene;

      const [moved] = shots.splice(oldIndex, 1);
      shots.splice(newIndex, 0, moved);

      // Update order values to match new array positions (1-based)
      const reordered = shots.map((s, i) => ({ ...s, order: i + 1 }));
      // …and repaint the display letters, or the board shows the old A/B/C against the new order.
      return { ...scene, shots: renumberShotsInOrder(reordered, project.documentSettings) };
    });

    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      const scene = updatedScenes.find(s => s.id === sceneId);
      if (scene) {
        const shotOrders = scene.shots.map(s => ({ shotId: s.id, order: s.order }));
        import('@/bridge/shotSyncBridge.ts').then(m => m.notifyReorder(sceneId, shotOrders));
      }
    }
  },

  /**
   * 20260923 - the board's select-mode drag (owner: "if all the selected shots are in the same scene I
   * can [press and hold to lift them and then drag to re-order them]"). The moved shots are taken in
   * their CURRENT array order whatever order `shotIds` lists them in, lifted out, and re-inserted as
   * ONE contiguous block at `insertIndex` — an index into the array with the moved shots removed
   * (shotDropSlotToInsertIndex). Ids not in the scene are ignored.
   *
   * Same wire as reorderShotInScene, deliberately: `reorderShots` already carries the WHOLE scene
   * order and Unity's HandleReorderShots applies any permutation, so a block move needs no new verb.
   * Kept as its own action rather than making the single one delegate, so the single-shot path (and
   * its pinned no-op/undo behaviour) stays byte-identical.
   *
   * An unchanged sequence writes nothing, sends nothing and leaves undo alone — a drop that changes
   * nothing must not cost the user their undo history.
   */
  reorderShotsInScene: (sceneId, shotIds, insertIndex) => {
    if (isReadOnly()) return;

    const { project } = get();
    if (!project) return;
    const scene = project.scenes.find(s => s.id === sceneId);
    if (!scene) return;

    const wanted = new Set(shotIds);
    const moved = scene.shots.filter(s => wanted.has(s.id));
    if (moved.length === 0) return;
    const remaining = scene.shots.filter(s => !wanted.has(s.id));
    const at = Math.max(0, Math.min(remaining.length, insertIndex));
    const next = [...remaining.slice(0, at), ...moved, ...remaining.slice(at)];
    if (next.every((s, i) => s.id === scene.shots[i].id)) return;

    if (capabilities.bridge) {
      // Same reasoning as reorderShotInScene: embedded, the mutation is committed natively and
      // irreversibly and undo is display-only, so the history is dropped rather than offered as a lie.
      useUndoStore.getState().clearStacks();
    } else {
      // Standalone: web-owned and genuinely reversible — do NOT collapse these two branches.
      useUndoStore.getState().pushSnapshot();
    }

    // Order values follow the new array positions (1-based), then the display letters are repainted.
    const reordered = next.map((s, i) => ({ ...s, order: i + 1 }));
    const updatedScene = { ...scene, shots: renumberShotsInOrder(reordered, project.documentSettings) };
    set({
      project: { ...project, scenes: project.scenes.map(s => (s.id === sceneId ? updatedScene : s)) },
    });

    if (capabilities.bridge) {
      const shotOrders = updatedScene.shots.map(s => ({ shotId: s.id, order: s.order }));
      import('@/bridge/shotSyncBridge.ts').then(m => m.notifyReorder(sceneId, shotOrders));
    }
  },

  /**
   * Append text to a shot's notes — the board's Sketch-with-AI Apply, which files the description
   * the user typed into the shot so the intent behind a generated frame survives in the document
   * (owner, 2026-08-25).
   *
   * Only the ADDITION crosses the bridge; Unity owns the read-modify-write against the real row.
   * That is what stops a stale web copy of `notes` from clobbering a note typed in the native Shot
   * Details popup between load and apply. This mirror applies the SAME rule locally — existing
   * notes + a blank line + the addition, no separator when there were none — so the Viewer's notes
   * line reflects it immediately; there is no echo to reconcile against.
   *
   * NO undo snapshot, deliberately — both callers depend on that: BoardEnhanceDialog appends
   * inside the same click as applyBatchGenerations, which already pushed one BEFORE the image
   * write, so one Undo rolls the WEB copy of both halves back together (a second snapshot here
   * would make the first Undo drop the note and keep the image); fileRationaleIntoShotNotes
   * (coverage apply) appends to an operation that is not undoable at all, so a snapshot would
   * dangle. Known residual: the bridge verb is append-only, so Unity keeps
   * the note through that Undo and the next repush resurrects it web-side — same asymmetry as the
   * already-shipped sendStaticImageToUnity path.
   */
  appendShotNotes: (shotId, text) => {
    if (isReadOnly()) return;
    const addition = text.trim();
    if (!addition) return;
    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => ({
      ...scene,
      shots: scene.shots.map(shot => {
        if (shot.id !== shotId) return shot;
        const base = (shot.notes ?? '').trimEnd();
        return { ...shot, notes: base === '' ? addition : `${base}\n\n${addition}` };
      }),
    }));
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/shotSyncBridge.ts').then(m => m.notifyShotNotesAppended(shotId, addition));
    }
  },

  // ── Transition helpers (delegates to transitionHelpers.ts) ──────────

  getTransition: (fromShotId, toShotId) => {
    const { project } = get();
    if (!project) return null;
    return getTransitionPure(project, fromShotId, toShotId);
  },

  getTransitionAtTime: (t) => {
    const { project } = get();
    if (!project) return null;
    return getTransitionAtTimePure(project, get().allShots(), t);
  },

  getContinuousChain: (shotId) => {
    const { project } = get();
    if (!project) return null;
    return getContinuousChainPure(project, get().allShots(), shotId);
  },

  getActiveContinuousPair: (shotId) => {
    const { project } = get();
    if (!project) return null;
    return getActiveContinuousPairPure(project, get().allShots(), shotId);
  },

  getContinuousInfo: (shotId) => {
    const { project } = get();
    if (!project) return null;
    return getContinuousInfoPure(project, get().allShots(), shotId);
  },

  setTransition: (fromShotId, toShotId, type) => {
    if (isReadOnly()) return;

    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const existing = project.transitions.find(
      t => t.fromShotId === fromShotId && t.toShotId === toShotId
    );

    if (existing?.videoObjectUrl && existing.type === 'continuous' && type !== 'continuous') {
      URL.revokeObjectURL(existing.videoObjectUrl);
    }

    let updatedTransitions: TimelineTransition[];

    if (type === 'cut') {
      if (existing?.videoObjectUrl) URL.revokeObjectURL(existing.videoObjectUrl);
      updatedTransitions = project.transitions.filter(
        t => !(t.fromShotId === fromShotId && t.toShotId === toShotId)
      );
    } else if (existing) {
      updatedTransitions = project.transitions.map(t =>
        t.fromShotId === fromShotId && t.toShotId === toShotId
          ? { ...t, type, duration: TRANSITION_DURATIONS[type] ?? 0.5,
              ...(type !== 'continuous' ? { videoObjectUrl: undefined, videoMimeType: undefined } : {}) }
          : t
      );
    } else {
      updatedTransitions = [
        ...project.transitions,
        {
          id: get()._allocId(),
          fromShotId,
          toShotId,
          type,
          duration: TRANSITION_DURATIONS[type] ?? 0.5,
          easing: 'linear',
        },
      ];
    }

    set({ project: { ...project, transitions: updatedTransitions } });
  },

  // ── Video helpers ─────────────────────────────────────────────────

  attachVideo: (shotId, objectUrl, mimeType, historyId?, opts?) => {
    if (isReadOnly()) return;

    // A sceneReady buffered up to 50 ms ago names videoObjectUrl too; drain it first or it lands
    // on top of this write and reverts the attach. See sceneReadyBuffer.ts (ORDERING, outbound).
    flushSceneReadyBuffer();

    useUndoStore.getState().pushSnapshot();
    const { project, videoShots } = get();
    if (!project) return;

    // Don't revoke — history entries may still reference this URL.
    // Pruning (aiStore.pruneOldBlobUrls) handles cleanup later.
    const updatedVideoShots = [
      ...videoShots.filter(v => v.shotId !== shotId),
      { shotId, objectUrl, mimeType, historyId },
    ];

    const updatedScenes = project.scenes.map(scene => ({
      ...scene,
      shots: scene.shots.map(shot =>
        shot.id === shotId ? { ...shot, videoObjectUrl: objectUrl, historyId: historyId ?? shot.historyId } : shot
      ),
    }));

    set({
      videoShots: updatedVideoShots,
      project: { ...project, scenes: updatedScenes },
    });

    // Extract audio track from video and add as SFX lane clip (fire-and-forget)
    extractAndStoreVideoAudio(objectUrl, shotId, () => get()._allocId()).catch(() => {});

    // Sync shot history_id FK to Unity so the canvas editor knows this shot has a video.
    // Passing assetType='video' tells Unity to clear any existing static image.
    if (capabilities.bridge && historyId) {
      // ONE continuation for both verbs, FK first (20260925, owner item 15d). They used to leave from
      // two separate dynamic-import chains, and in the production bundle the poster's chain (a module
      // already in the main chunk) beat the FK's (its own chunk, behind Vite's preload helper) — so
      // Unity stored the poster, then updateShotHistoryId{assetType:'video'} ran
      // ClearStaticImageForShotId and wiped it. The shot kept its stale 3D still forever (a reopen
      // included). The outbox is FIFO, so sending the FK first here IS the ordering guarantee.
      //
      // Extract the first frame of the video and send it to Unity as a poster-frame
      // thumbnail so the shot list shows a meaningful image instead of the live render.
      //
      // …unless the caller already has one. 20260909 (owner): "Add as new shot" (Create tab) inserts
      // the shot FIRST, and that insert's repush is a full loadProject whose revokeOldBlobUrls
      // (actions.ts) kills every generationHistory objectUrl — including the blob: this call is
      // attaching. Extracting HERE would then fail and the new shot would keep the anchor's live
      // render, so that caller captures the frame while the blob is still alive and hands it in.
      const suppliedPoster = opts?.posterBase64;
      void Promise.all([
        import('@/bridge/generationHistorySyncBridge.ts'),
        import('@/bridge/embeddedBridge.ts'),
      ]).then(([{ sendShotHistoryIdToUnity }, { enqueueSendToNative }]) => {
        sendShotHistoryIdToUnity(shotId, historyId, 'video');
        if (suppliedPoster) {
          enqueueSendToNative('setVideoPosterFrame', { shotId, imageData: suppliedPoster });
          return;
        }
        // The extraction fallback is chained off the SAME continuation, so it can only ever follow
        // the FK send (its own latency made that likely before; this makes it certain).
        return import('@/utils/videoFrame.ts')
          .then(({ extractVideoFirstFrame }) => extractVideoFirstFrame(objectUrl))
          .then(base64 => {
            enqueueSendToNative('setVideoPosterFrame', { shotId, imageData: base64 });
          })
          .catch(err => console.warn('[attachVideo] Poster frame extraction failed:', err));
      }).catch(err => console.warn('[attachVideo] Bridge send failed:', err));
    }
  },

  removeVideo: (shotId) => {
    if (isReadOnly()) return;

    // Same hazard as attachVideo, inverted: a buffered push carrying this shot's animatic URL
    // would restore the video this call is removing. Drain before the write.
    flushSceneReadyBuffer();

    useUndoStore.getState().pushSnapshot();
    const { project, videoShots } = get();
    if (!project) return;

    // Don't revoke — history entries may still reference this URL.
    // Pruning (aiStore.pruneOldBlobUrls) handles cleanup later.
    const updatedScenes = project.scenes.map(scene => ({
      ...scene,
      shots: scene.shots.map(shot =>
        shot.id === shotId ? { ...shot, videoObjectUrl: undefined, historyId: undefined } : shot
      ),
    }));

    set({
      videoShots: videoShots.filter(v => v.shotId !== shotId),
      project: { ...project, scenes: updatedScenes },
    });

    // Clear shot history_id FK in Unity
    if (capabilities.bridge) {
      import('@/bridge/generationHistorySyncBridge.ts').then(({ sendShotHistoryIdToUnity }) => {
        sendShotHistoryIdToUnity(shotId, null);
      });
    }
  },

  // ── Transition video helpers ─────────────────────────────────────

  attachTransitionVideo: (fromShotId, toShotId, objectUrl, mimeType, historyId?) => {
    if (isReadOnly()) return;

    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const updatedTransitions = project.transitions.map(t => {
      if (t.fromShotId === fromShotId && t.toShotId === toShotId) {
        if (t.videoObjectUrl) URL.revokeObjectURL(t.videoObjectUrl);
        return { ...t, videoObjectUrl: objectUrl, videoMimeType: mimeType, videoHistoryId: historyId };
      }
      return t;
    });

    set({ project: { ...project, transitions: updatedTransitions } });
  },

  removeTransitionVideo: (fromShotId, toShotId) => {
    if (isReadOnly()) return;


    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const updatedTransitions = project.transitions.map(t => {
      if (t.fromShotId === fromShotId && t.toShotId === toShotId) {
        if (t.videoObjectUrl) URL.revokeObjectURL(t.videoObjectUrl);
        return { ...t, videoObjectUrl: undefined, videoMimeType: undefined, videoHistoryId: undefined };
      }
      return t;
    });

    set({ project: { ...project, transitions: updatedTransitions } });
  },

  // ── Save ────────────────────────────────────────────────────────────

  setOriginalFile: (bytes, name) => set({ originalBytes: bytes, fileName: name }),

  // ── Review mode ──────────────────────────────────────────────────

  setReadOnly: (readOnly) => set({ readOnly }),
  setSessionId: (id) => set({ sessionId: id }),
  setSessionRevision: (rev) => set({ sessionRevision: rev }),
  setReviewSessionId: (id) => set({ reviewSessionId: id }),
  setIsCreator: (isCreator) => set({ isCreator }),

  // ── Progressive loading ──────────────────────────────────────────

  markSceneReady: (sceneId, shots) => {
    get().markScenesReady([{ sceneId, shots }]);
  },

  /**
   * Batch twin of markSceneReady — the whole of Unity's open-handshake burst in ONE set().
   * See store/sceneReadyBuffer.ts for why the burst is buffered rather than applied per message.
   *
   * Two things keep the write off the wire when there is nothing to say: applyTileUpdate returns
   * the shot unchanged when the merge is a no-op (on a live-board refresh the carry-over already
   * put the identical URLs on these rows, so nearly the whole burst is), and readySceneIds is
   * already fully populated on the embedded path (computeInitialSceneState). When both hold this
   * returns without calling set() at all — no new project reference, no board re-render.
   */
  markScenesReady: (entries) => {
    const { project, readySceneIds } = get();
    if (!project || entries.length === 0) return;

    // Collapse the batch per scene first (arrival order preserved by Map insertion order), so a
    // scene pushed twice in one burst is one pass over its shots rather than two.
    const byScene = new Map<number, Map<number, SceneReadyShotUpdate>>();
    for (const entry of entries) {
      let shots = byScene.get(entry.sceneId);
      if (!shots) {
        shots = new Map<number, SceneReadyShotUpdate>();
        byScene.set(entry.sceneId, shots);
      }
      for (const fresh of entry.shots) {
        const prev = shots.get(fresh.id);
        shots.set(fresh.id, prev ? mergeTileUpdates(prev, fresh) : fresh);
      }
    }

    const sceneById = new Map(project.scenes.map(s => [s.id, s]));
    // Only the shots whose merge actually changes something; a scene absent from this map is
    // left at its existing object identity below.
    const mergedByScene = new Map<number, Map<number, TimelineShot>>();
    for (const [sceneId, freshShots] of byScene) {
      const scene = sceneById.get(sceneId);
      const shotById = scene ? new Map(scene.shots.map(s => [s.id, s])) : null;
      let merged: Map<number, TimelineShot> | null = null;
      for (const fresh of freshShots.values()) {
        const shot = shotById?.get(fresh.id);
        // Updates for shots this store hasn't loaded yet — an unknown sceneId (add-scene) puts
        // the whole payload here — are stashed rather than dropped; see pendingTileUpdates.
        if (!shot) {
          stashPendingTileUpdate(fresh.id, {
            thumbnailDataUrl: fresh.thumbnailDataUrl,
            videoObjectUrl: fresh.videoObjectUrl,
            thumbHash: fresh.thumbHash,
            videoHash: fresh.videoHash,
            videoCleared: fresh.videoCleared,
          });
          continue;
        }
        const next = applyTileUpdate(shot, fresh);
        if (next === shot) continue;
        if (!merged) {
          merged = new Map<number, TimelineShot>();
          mergedByScene.set(sceneId, merged);
        }
        merged.set(shot.id, next);
      }
    }

    // A scene id the store has never seen still becomes ready (add-scene) — same as before.
    const hasNewReadyId = entries.some(e => !readySceneIds.has(e.sceneId));
    if (mergedByScene.size === 0 && !hasNewReadyId) return;

    const t0 = performance.now();
    let newReadyIds: Set<number> | null = null;
    if (hasNewReadyId) {
      newReadyIds = new Set(readySceneIds);
      for (const entry of entries) newReadyIds.add(entry.sceneId);
    }
    set({
      ...(mergedByScene.size > 0 ? {
        project: {
          ...project,
          scenes: project.scenes.map(scene => {
            const merged = mergedByScene.get(scene.id);
            if (!merged) return scene;
            return { ...scene, shots: scene.shots.map(shot => merged.get(shot.id) ?? shot) };
          }),
        },
      } : {}),
      ...(newReadyIds ? { readySceneIds: newReadyIds } : {}),
    });
    recordStoreSet(performance.now() - t0);
  },

  markAllScenesReady: () => {
    const { project } = get();
    if (!project) return;
    set({ readySceneIds: new Set(project.scenes.map(s => s.id)), allScenesReady: true, loadBurstSettled: true });
  },

  markLoadBurstPending: () => set({ loadBurstSettled: false }),

  markShotThumbnail: (shotId, url, hashes) => {
    const { project } = get();
    if (!project) return;
    // Not loaded yet (freshly added shot, reload still in flight) — stash, see pendingTileUpdates.
    if (!project.scenes.some((scene) => scene.shots.some((shot) => shot.id === shotId))) {
      stashPendingTileUpdate(shotId, {
        thumbnailDataUrl: url,
        thumbHash: hashes?.thumbHash,
        videoHash: hashes?.videoHash,
      });
      return;
    }
    const updatedScenes = project.scenes.map((scene) => ({
      ...scene,
      shots: scene.shots.map((shot) => (shot.id === shotId ? {
        ...shot,
        thumbnailDataUrl: url,
        thumbHash: hashes?.thumbHash ?? shot.thumbHash,
        videoHash: hashes?.videoHash ?? shot.videoHash,
      } : shot)),
    }));
    set({ project: { ...project, scenes: updatedScenes } });
  },

  setBoardFillState: (state) => set((s) => ({
    boardFill: state,
    // A FRESH fill (previous one absent or complete, this snapshot incomplete) starts un-dismissed;
    // mid-fill snapshots keep whatever the user chose, or the modal would slam back up on the next
    // poke. A resume re-scan arrives incomplete after an incomplete fill and is the same fill, so
    // it keeps the bit too.
    boardFillModalDismissed:
      (s.boardFill === null || s.boardFill.complete) && !state.complete ? false : s.boardFillModalDismissed,
  })),

  dismissBoardFillModal: () => set({ boardFillModalDismissed: true }),

  // ── Asset-freshness reconcile sweep (Task 14) ─────────────────────

  dropShots: (ids) => {
    const { project } = get();
    if (!project || ids.length === 0) return;
    const idSet = new Set(ids);
    const updatedScenes = project.scenes.map((scene) => ({
      ...scene,
      shots: scene.shots.filter((shot) => !idSet.has(shot.id)),
    }));
    set({ project: { ...project, scenes: updatedScenes } });
  },

  // ── Scene selector ──────────────────────────────────────────────────

  setActiveScene: (id) => {
    const { project } = get();
    if (!project) return;
    if (id === null) {
      set({ activeSceneId: null });
    } else {
      let offset = 0;
      for (const scene of project.scenes) {
        if (scene.id === id) break;
        offset += scene.shots.reduce((sum, s) => sum + s.duration, 0);
      }
      // Auto-select the first shot in the new scene
      const scene = project.scenes.find(s => s.id === id);
      const firstShot = scene?.shots[0] ?? null;
      set({ activeSceneId: id, selectedShotId: firstShot?.id ?? null });
      // Sync playback position to the start of the selected scene
      usePlaybackStore.setState({ currentTime: offset });
    }
  },

  getActiveScenes: () => {
    const { project, activeSceneId } = get();
    if (!project) return [];
    return getActiveScenesPure(project, activeSceneId);
  },

  getViewOffset: () => {
    const { project, activeSceneId } = get();
    if (!project || activeSceneId === null) return 0;
    return getViewOffsetPure(project, activeSceneId);
  },

  getViewDuration: () => {
    const { project, activeSceneId } = get();
    if (!project) return 0;
    return getViewDurationPure(project, activeSceneId);
  },

  // ── Derived helpers (delegates to projectDerived.ts) ────────────────

  allShots: () => {
    const { project } = get();
    if (!project) return [];
    return allShotsPure(project);
  },

  getShotAtTime: (t) => {
    const { project } = get();
    if (!project) return null;
    return getShotAtTimePure(project, t);
  },

  getShotStartTime: (id) => {
    const { project } = get();
    if (!project) return 0;
    return getShotStartTimePure(project, id);
  },

  getSceneForShot: (shotId) => {
    const { project } = get();
    if (!project) return null;
    return getSceneForShotPure(project, shotId);
  },

  getScriptProgress: () => {
    const { project, activeSceneId } = get();
    if (!project) return { scene: null, progress: 0 };
    const currentTime = usePlaybackStore.getState().currentTime;
    const offset = getViewOffsetPure(project, activeSceneId);
    return getScriptProgressPure(project, activeSceneId, currentTime, offset);
  },

  setCustomScriptText: (sceneId, text) => set(s => ({
    customScriptTexts: { ...s.customScriptTexts, [sceneId]: text },
  })),

  saveCustomScriptToScene: (sceneId) => {
    if (isReadOnly()) return;
    const { project, customScriptTexts } = get();
    if (!project) return;
    const text = customScriptTexts[sceneId];
    if (!text) return;
    useUndoStore.getState().pushSnapshot();
    const updatedScenes = project.scenes.map(scene =>
      scene.id === sceneId ? { ...scene, screenplayText: text } : scene
    );
    set({
      project: { ...project, scenes: updatedScenes, hasScreenplay: true },
    });
  },

  markScreenplayEdited: (sceneId) => set(s => ({
    editedScreenplaySceneIds: new Set(s.editedScreenplaySceneIds).add(sceneId),
  })),

  getAssetsForScene: (sceneId) => {
    const { project } = get();
    if (!project) return { videos: 0, images: 0, totalCredits: 0 };
    return getAssetsForScenePure(project, sceneId, useAiStore.getState().generationHistory);
  },

  setTeamAccessState: (has, url, sessionId?, revision?, password?) => {
    const sid = sessionId ?? null;
    set({
      hasTeamAccess: has,
      teamAccessUrl: url,
      teamSessionId: sid,
      teamRevision: revision ?? 0,
      teamSharePassword: password ?? null,
    });
    // In embedded mode, set review session when team access exists
    if (capabilities.bridge && sid) {
      set({ reviewSessionId: sid, isCreator: true });
    } else if (capabilities.bridge) {
      set({ reviewSessionId: null, isCreator: false });
    }
  },

  // ── Coverage (script lining) ─────────────────────────────────────

  assignElementsToShot: (sceneId, elementIds, shotId) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const idSet = new Set(elementIds.map(e => `${e.type}:${e.id}`));
    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId || !scene.screenplay) return scene;
      return {
        ...scene,
        screenplay: {
          ...scene.screenplay,
          dialogue: scene.screenplay.dialogue.map(d =>
            idSet.has(`dialogue:${d.id}`) ? { ...d, shotId } : d
          ),
          actions: scene.screenplay.actions.map(a =>
            idSet.has(`action:${a.id}`) ? { ...a, shotId } : a
          ),
        },
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m => m.notifyCoverageAssignment(elementIds, shotId));
    }
  },

  unassignElementsFromShot: (sceneId, elementIds, _shotId) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const idSet = new Set(elementIds.map(e => `${e.type}:${e.id}`));
    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId || !scene.screenplay) return scene;
      return {
        ...scene,
        screenplay: {
          ...scene.screenplay,
          dialogue: scene.screenplay.dialogue.map(d =>
            idSet.has(`dialogue:${d.id}`) ? { ...d, shotId: 0 } : d
          ),
          actions: scene.screenplay.actions.map(a =>
            idSet.has(`action:${a.id}`) ? { ...a, shotId: 0 } : a
          ),
        },
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m => m.notifyCoverageAssignment(elementIds, 0));
    }
  },

  updateShotCoverageProps: (shotId, updates) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => ({
      ...scene,
      shots: scene.shots.map(shot =>
        shot.id === shotId ? { ...shot, ...updates } : shot
      ),
    }));
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m => m.notifyShotPropertyUpdate(shotId, updates));
    }
  },

  /**
   * Replace one shot's coverage lines — the board panel's Coverage sub-view (v55
   * `screenplay_coverage_lines`), NOT the legacy scalar `element.shotId` the three actions above
   * write. Whole-shot replace, matching the bridge verb: the panel's Edit mode owns exactly one
   * shot's lining at a time, so unlike notes there is no concurrent native writer to merge against.
   * An empty `lines` array is a real value — it clears the shot's coverage.
   *
   * `coverageColor` is written locally in the same set() and sent in the same message, so a shot's
   * color stops being a palette index the moment it has coverage.
   *
   * Undo is Pattern C — the policy reorderShotInScene reasoned out (projectStore.ts:396-408,
   * cs:5467). On the bridge this mutation is committed natively and irreversibly (no inverse
   * coverage verb exists anywhere in the codebase), so every snapshot on the stack — not just the
   * one this action would push — now predates a change Undo cannot revert; drop the history rather
   * than offer a lie. clearStacks is idempotent, which is what makes it safe under a tap-cycle that
   * calls this once per tap. Off-bridge is unreachable today (Edit mode gates on
   * `canEdit = capabilities.bridge && !readOnly`) and kept for guard symmetry: if a standalone
   * editor ever lands, the mutation is web-owned and undo stays honest. Do NOT collapse the two.
   */
  setShotCoverageLines: (sceneId, shotId, lines, coverageColor) => {
    if (isReadOnly()) return;

    if (capabilities.bridge) {
      useUndoStore.getState().clearStacks();
    } else {
      useUndoStore.getState().pushSnapshot();
    }

    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId) return scene;
      return {
        ...scene,
        shots: scene.shots.map(shot =>
          shot.id === shotId ? { ...shot, coverageLines: [...lines], coverageColor } : shot
        ),
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m =>
        m.notifyShotCoverageLines(shotId, sceneId, lines, coverageColor));
    }
  },

  deleteScreenplayElements: (sceneId, elements) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const dialogueIds = new Set(elements.filter(e => e.type === 'dialogue').map(e => e.id));
    const actionIds = new Set(elements.filter(e => e.type === 'action').map(e => e.id));
    const transitionIds = new Set(elements.filter(e => e.type === 'transition').map(e => e.id));

    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId || !scene.screenplay) return scene;
      return {
        ...scene,
        screenplay: {
          ...scene.screenplay,
          dialogue: scene.screenplay.dialogue.filter(d => !dialogueIds.has(d.id)),
          actions: scene.screenplay.actions.filter(a => !actionIds.has(a.id)),
          transitions: scene.screenplay.transitions.filter(t => !transitionIds.has(t.id)),
        },
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m => m.notifyScreenplayElementsDeleted(elements));
    }
  },

  updateScreenplayElementText: (sceneId, elementType, elementId, text) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId || !scene.screenplay) return scene;
      return {
        ...scene,
        screenplay: {
          ...scene.screenplay,
          dialogue: elementType === 'dialogue'
            ? scene.screenplay.dialogue.map(d => d.id === elementId ? { ...d, dialogueText: text } : d)
            : scene.screenplay.dialogue,
          actions: elementType === 'action'
            ? scene.screenplay.actions.map(a => a.id === elementId ? { ...a, actionText: text } : a)
            : scene.screenplay.actions,
          transitions: elementType === 'transition'
            ? scene.screenplay.transitions.map(t => t.id === elementId ? { ...t, transitionText: text } : t)
            : scene.screenplay.transitions,
        },
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m => m.notifyScreenplayElementUpdated(elementType, elementId, text));
    }
  },

  updateDialogueCharacterName: (sceneId, dialogueId, characterName) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => {
      if (scene.id !== sceneId || !scene.screenplay) return scene;
      return {
        ...scene,
        screenplay: {
          ...scene.screenplay,
          dialogue: scene.screenplay.dialogue.map(d =>
            d.id === dialogueId ? { ...d, characterName } : d,
          ),
        },
      };
    });
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/coverageSyncBridge.ts').then(m =>
        m.notifyDialogueCharacterNameUpdated(dialogueId, characterName),
      );
    }
  },

  updateLocationDescription: (locationId, text) => {
    if (isReadOnly()) return;
    // 20260921 (P2-5) - the same temp-id backstop the character actions carry (see
    // renameScreenplayCharacter below). A duplicate's optimistic copy holds a NEGATIVE id until Unity's
    // repush swaps in the real one, and Unity refuses any id <= 0 before it writes — so applying the
    // mirror here would report success and discard the paragraph the user typed. Ahead of pushSnapshot,
    // so a refused edit does not spend an undo slot either. LocationDetail disables the box too; this is
    // the backstop for any other caller.
    if (locationId <= 0) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        screenplayLocations: (project.screenplayLocations ?? []).map(l =>
          l.id === locationId ? { ...l, description: text } : l
        ),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifyLocationDescriptionUpdated(locationId, text));
    }
  },

  updateSceneLocationNotes: (sceneId, text) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot();
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        scenes: project.scenes.map(s => (s.id === sceneId ? { ...s, locationNotes: text } : s)),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifySceneLocationNotesUpdated(sceneId, text));
    }
  },

  // 20260923 - the per-scene PENCIL on a Locations card (owner: "the user can press the pencil button to
  // edit JUST that extra text"). A mirror of Unity's SetSceneLocationTextCore: the scene's own location
  // text — its heading, which every composer/export/prompt reads — changes; its LINK does not. The pencil
  // never changes membership, so the scene stays on the same card whatever it now reads.
  setSceneLocationText: (sceneId, text) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // Unity's refusals, ahead of pushSnapshot so a refused edit spends no undo slot: a temp/zero id, and
    // an empty text — taking a scene's location away is the trash's job (unlinkSceneLocation), not an
    // empty save. An unchanged text sends nothing.
    if (sceneId <= 0) return;
    const clean = canonicalizeLocationName(text);
    if (!clean) return;
    const scene = project.scenes.find(s => s.id === sceneId);
    if (!scene || scene.name === clean) return;

    // pushSnapshot — updateSceneLocationNotes' policy, deliberately. Both are fire-and-forget text writes
    // to ONE scene (no repush, no row created or destroyed), sitting side by side on the same scene row,
    // so they answer undo the same way; undo re-sets the web store without re-sending a verb for either,
    // the trade the notes box already makes. Contrast unlinkSceneLocation below, which may make Unity DROP
    // a row and therefore clears the history instead.
    useUndoStore.getState().pushSnapshot();
    set({
      project: {
        ...project,
        scenes: project.scenes.map(s => (s.id === sceneId ? { ...s, name: clean } : s)),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifySceneLocationTextUpdated(sceneId, clean));
    }
  },

  // 20260923 - the per-scene TRASH on a Locations card (owner: "the delete button to remove the location
  // from just that scene"). A mirror of Unity's UnlinkSceneLocationCore: FK 0, the text KEPT — the scene
  // moves to Unassigned with its slug line intact — then the M5 orphan rule on the row it left.
  unlinkSceneLocation: (sceneId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // Unity refuses `sceneId <= 0` and a scene with no link; so does the mirror, ahead of clearStacks so
    // a refused unlink costs no undo history.
    if (sceneId <= 0) return;
    const scene = project.scenes.find(s => s.id === sceneId);
    const previousLocationId = scene?.screenplayLocationId ?? 0;
    if (!scene || previousLocationId <= 0) return;

    // clearStacks, NOT pushSnapshot — deleteScreenplayLocation's reasoning: Unity commits the unlink
    // natively and may DELETE the row it left (M5), and undo never re-sends a verb, so an undo would put
    // the scene back on a card the .previs no longer has.
    useUndoStore.getState().clearStacks();
    const scenes = project.scenes.map(s => (s.id === sceneId ? { ...s, screenplayLocationId: 0 } : s));

    // M5 mirror, so the card does not flash empty before the repush: Unity's
    // DeleteScreenplayLocationIfOrphanedCore drops the row once no (non-hidden) scene references it,
    // unless it carries description / set_key / llm_blocking. The store holds no hidden scenes, so its
    // refcount is the same one Unity takes. The repush that follows is the truth either way.
    const list = project.screenplayLocations ?? [];
    const row = list.find(l => l.id === previousLocationId);
    const orphaned = !!row
      && !scenes.some(s => s.screenplayLocationId === previousLocationId)
      && !row.description && !row.setKey && !row.llmBlocking;
    set({
      project: {
        ...project,
        scenes,
        ...(orphaned ? { screenplayLocations: list.filter(l => l.id !== previousLocationId) } : {}),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifySceneLocationUnlinked(sceneId));
    }
  },

  addScreenplayLocation: (name) => {
    if (isReadOnly()) return;
    useUndoStore.getState().pushSnapshot(); // matches the two sibling location actions above
    const { project } = get();
    if (!project) return;
    const clean = canonicalizeLocationName(name);
    if (!clean) return;
    const list = project.screenplayLocations ?? [];
    // Idempotent by the same identity rule Unity uses (trim → collapse → uppercase), so a
    // duplicate add is a no-op on both sides rather than a phantom web-only row.
    const key = normalizeLocationKey(clean);
    if (list.some(l => normalizeLocationKey(l.name) === key)) return;
    // NEGATIVE temp id: IdentifierManager only mints positives, so it can never collide
    // with a real row id; the next repush replaces the whole array with Unity's truth.
    const tempId = Math.min(0, ...list.map(l => l.id)) - 1;
    set({
      project: {
        ...project,
        screenplayLocations: [...list, { id: tempId, name: clean, interiorExterior: '' }],
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifyScreenplayLocationAdded(clean));
    }
  },

  renameScreenplayLocation: (locationId, name) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // 20260921 (P2-5) - temp-id backstop, identical to renameScreenplayCharacter's: a duplicate's
    // optimistic copy carries a NEGATIVE id until Unity's repush replaces it, Unity refuses any id <= 0,
    // and the rename below rewrites the linked scenes' texts too — so a mirror applied here
    // would leave the two sides disagreeing about a name with no repush coming to settle it. BrowseMode
    // hides the affordance as well; this is the backstop.
    if (locationId <= 0) return;

    // Shared with locationGrouping and the Locations tab (see @/utils/locationName): canonicalization
    // matching Unity. This has to agree, because a rename is fire-and-forget — the mirror below is all
    // the web will show until the next repush, so a different canonical form would be a visible lie.
    const clean = canonicalizeLocationName(name);
    if (!clean) return;

    const list = project.screenplayLocations ?? [];
    const target = list.find(l => l.id === locationId);
    if (!target || target.name === clean) return;

    // Defence in depth: the caller validates duplicates against the same rule and shows an inline
    // error. Unity refuses a colliding rename too, so a store that let one through would just diverge.
    const key = normalizeLocationKey(clean);
    if (list.some(l => l.id !== locationId && normalizeLocationKey(l.name) === key)) return;

    useUndoStore.getState().pushSnapshot();
    set({
      project: {
        ...project,
        screenplayLocations: list.map(l => (l.id === locationId ? { ...l, name: clean } : l)),
        // 20260923 - each linked scene's OWN text, per Unity's RenameScreenplayLocationCore: the old name
        // at the front is swapped for the new one and the rest kept verbatim ("BARN (OLD JOE MEMORY)" →
        // "RED BARN (OLD JOE MEMORY)"); a text that never started with it (a merged-in "HAYLOFT") is left
        // alone. It used to overwrite every linked text with the bare row name.
        scenes: project.scenes.map(s => {
          if (s.screenplayLocationId !== locationId) return s;
          const renamed = renameLocationInSceneText(s.name ?? '', target.name, clean);
          return renamed === s.name ? s : { ...s, name: renamed };
        }),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifyScreenplayLocationRenamed(locationId, clean));
    }
  },

  duplicateScreenplayLocation: (locationId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // 20260921 (P2-5) - same temp-id backstop as the rename above, and the same one
    // duplicateScreenplayCharacter carries: duplicating a copy that Unity has not acknowledged yet would
    // append a SECOND phantom row (id -2) for a verb Unity refuses at `sourceLocationId <= 0`.
    if (locationId <= 0) return;

    const list = project.screenplayLocations ?? [];
    const source = list.find(l => l.id === locationId);
    if (!source) return;

    // Source name + the lowest free "-N" (N >= 2), computed against the loaded rows. Unity re-checks
    // identity against ALL rows and refuses a collision, so this is a proposal, not the final word.
    const name = nextDuplicateLocationName(source.name, list.map(l => l.name));

    // pushSnapshot, NOT clearStacks: unlike delete, a duplicate destroys nothing — undoing it leaves a
    // stray row that the user can delete, so the history is still honest.
    useUndoStore.getState().pushSnapshot();

    // NEGATIVE temp id, same reasoning as addScreenplayLocation: IdentifierManager only mints
    // positives, so this can never collide with a real row, and the next repush replaces the whole
    // array with Unity's truth. Every payload field the store holds is copied — the row is a full copy.
    const tempId = Math.min(0, ...list.map(l => l.id)) - 1;
    set({
      project: {
        ...project,
        screenplayLocations: [...list, { ...source, id: tempId, name }],
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifyScreenplayLocationDuplicated(locationId, name));
    }
  },

  deleteScreenplayLocation: (locationId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // 20260921 (P2-5) - temp-id backstop, and the loudest of the four: Unity's
    // HandleDeleteScreenplayLocation returns on `locationId <= 0` above its own log line, so a delete on
    // a not-yet-acknowledged duplicate wrote nothing, logged nothing and surfaced nothing — while the
    // clearStacks() below had ALREADY destroyed the whole undo history for a change that never happened,
    // and the row came back on the next repush. Ahead of that call on purpose.
    if (locationId <= 0) return;
    if (!(project.screenplayLocations ?? []).some(l => l.id === locationId)) return;

    // clearStacks, NOT pushSnapshot — the same call reorderScene makes above, for the same reason.
    // This delete is committed natively, irreversibly, and never repushes: Unity has already dropped
    // the row and unlinked its scenes by the time any undo could run, and undo re-sets web stores
    // without ever re-sending a bridge verb. Offering "undo" here would put a location back on screen
    // that no longer exists in the .previs — a lie that the next repush silently corrects. Every older
    // snapshot predates the same unrevertable change, so the whole history goes.
    useUndoStore.getState().clearStacks();
    set({
      project: {
        ...project,
        screenplayLocations: (project.screenplayLocations ?? []).filter(l => l.id !== locationId),
        // 20260923 - affected scenes are UNLINKED (FK 0) and KEEP their text, as Unity's
        // DeleteScreenplayLocationExplicitCore now does: they land in Unassigned with their headings
        // intact instead of blank. locationType / timeOfDay are scene attributes and survive as before.
        scenes: project.scenes.map(s =>
          s.screenplayLocationId === locationId ? { ...s, screenplayLocationId: 0 } : s
        ),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m => m.notifyScreenplayLocationDeleted(locationId));
    }
  },

  // 20260923 - Locations MERGE (owner: "the Location tab doesn't have that functionality; I can't merge
  // locations"). A MIRROR of Unity's MergeScreenplayLocationCore, which owns the write and repushes either
  // way — nothing here allocates or decides anything Unity does not.
  mergeScreenplayLocation: (sourceLocationId, targetLocationId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // The same refusals Unity makes, ahead of clearStacks so a refused merge costs no undo history:
    // a temp id (an unacknowledged duplicate/add — Unity refuses `<= 0`), a merge onto itself, or a row
    // the store does not hold.
    if (sourceLocationId <= 0 || targetLocationId <= 0 || sourceLocationId === targetLocationId) return;
    const list = project.screenplayLocations ?? [];
    const source = list.find(l => l.id === sourceLocationId);
    const target = list.find(l => l.id === targetLocationId);
    if (!source || !target) return;

    // clearStacks, NOT pushSnapshot — the merge precedent (mergeSceneScreenplay / mergeCharacterDialogue):
    // Unity commits it irreversibly and undo never re-sends a bridge verb, so every snapshot on the stack
    // now predates a change undo cannot revert.
    useUndoStore.getState().clearStacks();

    // The target FILLS ITS BLANKS from the source — exactly Unity's rule (the target's own non-empty value
    // wins), so the mirror and the file agree until the repush replaces both rows with Unity's truth.
    const fill = (own: string | undefined, other: string | undefined) => (own ? own : other);
    const merged = {
      ...target,
      description: fill(target.description, source.description),
      llmBlocking: fill(target.llmBlocking, source.llmBlocking),
      setKey: fill(target.setKey, source.setKey),
      setVariationKey: fill(target.setVariationKey, source.setVariationKey),
    };
    set({
      project: {
        ...project,
        screenplayLocations: list
          .filter(l => l.id !== sourceLocationId)
          .map(l => (l.id === targetLocationId ? merged : l)),
        // 20260923 - the FK ONLY. Each scene keeps its own text (owner: merging stripped "(OLD JOE MEMORY)"
        // and "changed the slug line so now it won't match things like printed scripts"); on v52 the FK
        // alone decides grouping, so the relinked scene files under the target whatever it reads.
        scenes: project.scenes.map(s =>
          s.screenplayLocationId === sourceLocationId ? { ...s, screenplayLocationId: targetLocationId } : s
        ),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/locationBridge.ts').then(m =>
        m.notifyScreenplayLocationMerged(sourceLocationId, targetLocationId));
    }
  },

  // ── Cast members (Script tab, Characters) ─────────────────────────
  // Rename/Duplicate mirror two of the three verbs Locations has. There is deliberately no ADD
  // action: adding a cast member is a native takeover (the Avatar Customizer collects the name and
  // Unity adds the row from the bake), so there is nothing to mirror until the repush — the verb is
  // sent straight from the Script tab, see bridge/characterBridge.ts.
  //
  // Delete is NOT here either: it is mergeCharacterDialogue(name, '') below, whose Unity branch
  // unassigns the dialogue and then runs the full cast + custom-character cleanup — a second
  // deletion path would be a liability.

  renameScreenplayCharacter: (characterId, name) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    // A duplicate's optimistic row carries a NEGATIVE temp id until Unity's repush replaces it, and
    // Unity refuses any id <= 0 — so applying the mirror here would leave the two sides disagreeing
    // with no repush coming to settle it. BrowseMode hides the affordance too; this is the backstop.
    if (characterId <= 0) return;

    const clean = canonicalizeCharacterName(name);
    if (!clean) return;

    const roster = project.screenplayCharacters ?? [];
    const target = roster.find(c => c.id === characterId);
    if (!target || target.name === clean) return;

    // Defence in depth: BrowseMode validates the same collision and shows an inline error, and Unity
    // refuses it independently. A store that let one through would just diverge from the .previs.
    const key = normalizeCharacterKey(clean);
    if (roster.some(c => c.id !== characterId && normalizeCharacterKey(c.name) === key)) return;

    // `screenplay_dialogue` stores speaker IDs, never the name, so Unity's rename needs no dialogue
    // write and the next load resolves every line to the new name on its own. The local mirror does
    // have to be rewritten by hand, because `ScreenplayDialogue.characterName` is a resolved display
    // string — and a multi-speaker line encodes its speaker SET as "A / B", so the rename has to
    // reach INSIDE that string. Whole-name first (a cast member genuinely called "AC/DC GUY" must
    // not be split), then per-token, exactly the rule BrowseMode's cast list and CharacterDetail's
    // line matcher use.
    const oldKey = normalizeCharacterKey(target.name);
    // `characters.name` is NOT NULL DEFAULT('') so a blank cast row is representable. Renaming one is
    // meaningless, and worse, an empty oldKey would match every SPEAKER-LESS dialogue line below and
    // drag the whole Unassigned bucket onto the new name. BrowseMode never offers such a row; this
    // guard is what makes the action safe to call directly.
    if (!oldKey) return;
    const renameSpeakers = (raw: string): string => {
      if (normalizeCharacterKey(raw) === oldKey) return clean;
      const parts = raw.split(/\s*\/\s*/);
      if (parts.length < 2) return raw;
      let changed = false;
      const mapped = parts.map(p => {
        if (normalizeCharacterKey(p) !== oldKey) return p.trim();
        changed = true;
        return clean;
      });
      return changed ? mapped.join(' / ') : raw;
    };

    useUndoStore.getState().pushSnapshot();
    set({
      project: {
        ...project,
        screenplayCharacters: roster.map(c => (c.id === characterId ? { ...c, name: clean } : c)),
        scenes: project.scenes.map(scene => {
          if (!scene.screenplay) return scene;
          const dialogue = scene.screenplay.dialogue.map(d => {
            const renamed = renameSpeakers(d.characterName);
            return renamed === d.characterName ? d : { ...d, characterName: renamed };
          });
          if (dialogue.every((d, i) => d === scene.screenplay!.dialogue[i])) return scene;
          return { ...scene, screenplay: { ...scene.screenplay, dialogue } };
        }),
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/characterBridge.ts').then(m => m.notifyScreenplayCharacterRenamed(characterId, clean));
    }
  },

  duplicateScreenplayCharacter: (characterId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;
    if (characterId <= 0) return; // same temp-id backstop as the rename above

    const roster = project.screenplayCharacters ?? [];
    const source = roster.find(c => c.id === characterId);
    if (!source) return;

    // A proposal only — Unity's Cast Library duplicate names the copy itself and the repush that
    // follows replaces this row wholesale. It exists so the new card carries a plausible name for
    // the round-trip rather than flashing the source's name back at the user.
    const name = nextDuplicateCharacterName(source.name, roster.map(c => c.name));
    // Same blank-cast-row case as the rename guard above: copying a nameless row would add a second
    // nameless one that the Cast list cannot even render (it keys on the uppercased name).
    if (!name) return;

    // pushSnapshot, NOT clearStacks: like a location duplicate, this destroys nothing — undoing it
    // leaves a stray cast member the user can delete, so the history is still honest.
    useUndoStore.getState().pushSnapshot();

    // NEGATIVE temp id, same reasoning as the location duplicate: IdentifierManager only mints
    // positives, so it can never collide with a real characterid, and BrowseMode gates the
    // id-requiring affordances on id > 0 — so the placeholder row cannot be renamed or duplicated
    // again before the repush swaps in Unity's real id.
    const tempId = Math.min(0, ...roster.map(c => c.id)) - 1;
    set({
      project: {
        ...project,
        // castPlaceholder is forced false to match what Unity writes: a duplicate never enters the
        // auto-cast pipeline. It copies the body (a shared customcharacterid is legal — deletion is
        // refcount-guarded) but starts with zero dialogue lines, so nothing else is carried here.
        screenplayCharacters: [...roster, { ...source, id: tempId, name, castPlaceholder: false }],
      },
    });
    if (capabilities.bridge) {
      import('@/bridge/characterBridge.ts').then(m => m.notifyScreenplayCharacterDuplicated(characterId));
    }
  },

  // ── Scene/character restructuring (View Changes mode) ─────────────

  reorderScene: (sceneId, newIndex) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;

    const scenes = [...project.scenes];
    const oldIndex = scenes.findIndex(s => s.id === sceneId);
    if (oldIndex < 0 || oldIndex === newIndex) return;

    const [moved] = scenes.splice(oldIndex, 1);
    scenes.splice(newIndex, 0, moved);

    const renumbered = renumberScenesInOrder(scenes, project.documentSettings);

    // Undo is display-only (it re-sets web stores, it never re-sends a bridge verb) and this
    // mutation is committed natively and irreversibly. Every snapshot on the stack — not just the
    // one this action would push — now predates a change undo cannot revert, so drop the history
    // instead of offering a lie. loadProject()'s clearStacks (actions.ts:407) does the same on the
    // repush that follows most structure actions, but reorder deliberately never repushes on
    // success (TimelineWebViewPanel.cs:3480-3482) — only on Unity's rejection — so this is the only
    // clear the success path ever gets.
    useUndoStore.getState().clearStacks();
    set({ project: { ...project, scenes: renumbered } });

    if (capabilities.bridge) {
      const sceneOrder = renumbered.map(s => s.id);
      import('@/bridge/embeddedBridge.ts').then(m =>
        m.enqueueSendToNative('reorderScenes', { sceneOrder }),
      );
    }
  },

  mergeSceneScreenplay: (sourceSceneId, targetSceneId) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;

    const sourceScene = project.scenes.find(s => s.id === sourceSceneId);
    const targetScene = project.scenes.find(s => s.id === targetSceneId);
    if (!sourceScene || !targetScene) return;

    // Delete audio clips for all shots in the source scene (scene is being removed)
    for (const shot of sourceScene.shots) {
      useAudioStore.getState().deleteClipsForShot(shot.id);
    }

    const sourceSp = sourceScene.screenplay;
    const targetSp = targetScene.screenplay;

    const mergedScreenplay: ScreenplayData = {
      dialogue: [
        ...(targetSp?.dialogue ?? []),
        ...(sourceSp?.dialogue ?? []).map(d => ({ ...d, sceneId: targetSceneId })),
      ],
      actions: [
        ...(targetSp?.actions ?? []),
        ...(sourceSp?.actions ?? []).map(a => ({ ...a, sceneId: targetSceneId })),
      ],
      transitions: [
        ...(targetSp?.transitions ?? []),
        ...(sourceSp?.transitions ?? []).map(t => ({ ...t, sceneId: targetSceneId })),
      ],
    };

    const updatedScenes = project.scenes
      .map(s => s.id === targetSceneId ? { ...s, screenplay: mergedScreenplay } : s)
      .filter(s => s.id !== sourceSceneId);

    // Dropping a scene shifts every later scene up one slot, so the survivors need the same
    // locked/override-aware renumbering a reorder does. Unity renumbers too and repushes right after,
    // but the Board tab renders from this store first — without it the tab shows a gap in the numbering
    // for the frame or two before the repush arrives.
    const renumbered = renumberScenesInOrder(updatedScenes, project.documentSettings);

    // Undo is display-only (it re-sets web stores, it never re-sends a bridge verb) and this
    // mutation is committed natively and irreversibly (source scene + its audio are gone from the
    // .previs). Every snapshot on the stack — not just the one this action would push — now
    // predates a change undo cannot revert, so drop the history instead of offering a lie.
    // loadProject()'s clearStacks (actions.ts:407) does the same on the repush that now follows
    // either outcome (TimelineWebViewPanel.cs:3496-3508 repushes unconditionally, success or
    // rejection); this closes the async window before that repush lands and covers the Screenplay
    // Manager popup host, which shares this handler but never repushes at all.
    useUndoStore.getState().clearStacks();
    set({ project: { ...project, scenes: renumbered } });

    if (capabilities.bridge) {
      import('@/bridge/embeddedBridge.ts').then(m =>
        m.enqueueSendToNative('mergeSceneScreenplay', { sourceSceneId, targetSceneId }),
      );
    }
  },

  mergeCharacterDialogue: (sourceCharName, targetCharName) => {
    if (isReadOnly()) return;
    const { project } = get();
    if (!project) return;

    const updatedScenes = project.scenes.map(scene => {
      if (!scene.screenplay) return scene;
      const updatedDialogue = scene.screenplay.dialogue.map(d =>
        d.characterName.toUpperCase() === sourceCharName.toUpperCase()
          ? { ...d, characterName: targetCharName }
          : d,
      );
      const hasChange = updatedDialogue.some((d, i) => d !== scene.screenplay!.dialogue[i]);
      if (!hasChange) return scene;
      return { ...scene, screenplay: { ...scene.screenplay, dialogue: updatedDialogue } };
    });

    // Undo is display-only (it re-sets web stores, it never re-sends a bridge verb) and this
    // mutation is committed natively and irreversibly (source character's dialogue reattributed,
    // then the character and its cast/customcharacter rows deleted). Every snapshot on the stack —
    // not just the one this action would push — now predates a change undo cannot revert, so drop
    // the history instead of offering a lie. loadProject()'s clearStacks (actions.ts:407) does the
    // same on the repush that now follows either outcome (TimelineWebViewPanel.cs:3510-3519 repushes
    // unconditionally, success or rejection); this closes the async window before that repush lands
    // and covers the Screenplay Manager popup host, which shares this handler but never repushes at
    // all. Clearing is correct even when `hasChange` is false for every scene above (no local
    // dialogue line matched sourceCharName) — Unity still deletes the source character's cast row,
    // so a web-side no-op here is not a native no-op.
    useUndoStore.getState().clearStacks();
    set({ project: { ...project, scenes: updatedScenes } });

    if (capabilities.bridge) {
      import('@/bridge/embeddedBridge.ts').then(m =>
        m.enqueueSendToNative('mergeCharacterDialogue', { sourceCharName, targetCharName }),
      );
    }
  },
}));

// The buffer holds pushes; this store applies them. Installed here rather than imported there so
// the dependency stays one-directional (store → buffer) and the tile writers above can flush
// synchronously before they write — see sceneReadyBuffer.ts's DEPENDENCY SHAPE note.
setSceneReadyApplier((entries) => useProjectStore.getState().markScenesReady(entries));

// ── Video retime helpers (awaitable, not store methods) ──────────────

/** Load a video element's metadata with a 3s fallback timeout. */
function loadVideoMetadata(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    vid.onloadedmetadata = () => resolve(vid.duration);
    vid.onerror = () => reject(new Error('Failed to load video metadata'));
    setTimeout(() => resolve(vid.duration), 3000);
    vid.src = url;
  });
}

/** Retime a single shot to match its video duration. */
export async function retimeShotToVideo(shotId: number, videoUrl: string): Promise<void> {
  try {
    const duration = await loadVideoMetadata(videoUrl);
    if (duration && isFinite(duration)) {
      useProjectStore.getState().updateShotDuration(shotId, duration, { pushUndo: false });
    }
  } catch { /* non-critical */ }
}

/** Retime two shots proportionally to match their transition video duration. */
export async function retimeTransitionToVideo(
  fromShotId: number, toShotId: number, videoUrl: string,
): Promise<void> {
  try {
    const videoDuration = await loadVideoMetadata(videoUrl);
    if (!isFinite(videoDuration) || videoDuration <= 0) return;
    const ps = useProjectStore.getState();
    const allShotsArr = ps.allShots();
    const fShot = allShotsArr.find(s => s.id === fromShotId);
    const tShot = allShotsArr.find(s => s.id === toShotId);
    if (!fShot || !tShot) return;
    const combined = fShot.duration + tShot.duration;
    if (combined <= 0) return;
    const ratio = videoDuration / combined;
    ps.updateShotDuration(fromShotId, fShot.duration * ratio, { pushUndo: false });
    ps.updateShotDuration(toShotId, tShot.duration * ratio, { pushUndo: false });
  } catch { /* non-critical */ }
}
